import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Divider,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useSelector } from 'react-redux';
import {
  getTeamsData,
  getSearchValue,
  getFacilitiesData,
  getStaffData,
} from '../../licenseAnalyticsSupport/getLicenseAnalyticsActions';
import { ClubNameCell } from '../finance/academyTableComponent';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { ageGroupNaming, positionAchievedNaming } from '../../../../api/typesDc';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose } from '@fortawesome/free-solid-svg-icons';
import './teamsComponent'; // Import the CSS file
import { DayLabels } from '../../../../api/types';

const formatTime = (date:any) => {
  // Create a Date object from the datetime string
  const dateObject = new Date(date);

  // Extract the hours and minutes
  const hours = dateObject
    .getHours()
    .toString()
    .padStart(2, '0');
  const minutes = dateObject
    .getMinutes()
    .toString()
    .padStart(2, '0');

  // Format as "HH:MM"
  return `${hours}:${minutes}`;
};

const getStaffSupervisor = (coachId:any, staffData:any) => {
  if (coachId && coachId !== -1) {
    let staff = staffData?.find((s:any) => s.id == coachId);
    return staff?.kanjiName;
  }
  return -1;
};

const getStaffGkCoaches = (coachId:any, staffData:any) => {
  if (coachId && coachId !== -1) {
    let staff = staffData?.find((s:any) => s.id == coachId);
    return staff?.kanjiName;
  }
  return -1;
};

const getStaffCoaches = (coachId:any, staffData:any) => {
  if (coachId !== -1) {
    let staff = staffData?.find((s:any) => s.id == coachId);
    return staff?.kanjiName;
  }
  return -1;
};

const getMedicalStaff = (coachId:any, staffData:any) => {
  if (coachId !== -1) {
    let staff = staffData?.find((s:any) => s.id == coachId);
    return staff?.kanjiName;
  }
  return -1;
};

const getPracticeGrounds = (grounds:any, facilities:any) => {
  let practiceGrounds: any = [];
  grounds?.map((groundId:any) => {
    if (groundId && groundId !== -1) {
      let ground = facilities?.find((s:any) => s.id == groundId);
      ground && Object.keys(ground).includes('name') && practiceGrounds.push(ground?.name);
    }
  });
  return practiceGrounds.length > 0 ? practiceGrounds.join(', ') : undefined;
};

const TeamDetail:any = React.memo(({ clubData, staffData, facilities, setSelectedRow }:any) => {
  const [t] = useTranslation();

  if (!clubData) return null;

  const { teamDetails, teamFees } = clubData;

  return (
    <>
      <div
        style={{
          backgroundColor: '#f3f6fb',
          fontSize: '16px',
          fontWeight: 'bold',
          paddingBottom: '5px',
          width: '100%',
          textAlign: 'left',
        }}
      >
        <ClubNameCell clubData={clubData} getImageUrl={(data:any) => data?.club?.imagePath} />
      </div>
      <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
        <Typography sx={{ width: '33%', flexShrink: 0, fontWeight: 'bold', fontFamily: 'Montserrat', padding: '5px 16px' }}>
          {t('INDIVIDUAL_TEAM_DETAIL')}
        </Typography>
        <div style={{ alignSelf: 'center', paddingRight: '10px' }}>
          <Tooltip title={'Close'}>
            <FontAwesomeIcon
              className="icon ml10"
              icon={faClose}
              style={{ cursor: 'pointer', fontSize: 18 }}
              onClick={() => setSelectedRow(null)}
            />
          </Tooltip>
        </div>
      </div>
      <Divider />
      <TableContainer sx={{ maxHeight: 500 }}>
        <Table stickyHeader aria-label="collapsible table">
          <TableRow className="sticky-header">
            <TableCell style={{ fontWeight: 'bold', padding: '0px 16px' }}>{t('NAME')}</TableCell>
            <TableCell style={{ fontWeight: 'bold', padding: '0px 16px' }}>{t('DC_7_ESTABLISHED')}</TableCell>
            <TableCell style={{ fontWeight: 'bold', padding: '0px 16px' }}>{t('DC_7_AGE_GROUP')}</TableCell>
            <TableCell style={{ fontWeight: 'bold', padding: '0px 16px' }}>{t('DC_7_STAFF_SUPERVISOR')}</TableCell>
            <TableCell style={{ fontWeight: 'bold', padding: '0px 16px' }}>{t('DC_7_STAFF_COACHES')}</TableCell>
            <TableCell style={{ fontWeight: 'bold', padding: '0px 16px' }}>{t('DC_7_STAFF_GK_COACHES')}</TableCell>
            <TableCell style={{ fontWeight: 'bold', padding: '0px 16px' }}>{t('DC_7_MEDICAL_STAFF')}</TableCell>
            <TableCell style={{ fontWeight: 'bold', padding: '0px 16px' }}>{t('DC_7_PRACTICE_GROUNDS')}</TableCell>
            <TableCell style={{ fontWeight: 'bold', padding: '0px 16px' }}>{t('DC_7_ADMISSION_FEES')}</TableCell>
            <TableCell style={{ fontWeight: 'bold', padding: '0px 16px' }}>{t('DC_7_PLAYER_MO_EXPENSE')}</TableCell>
            <TableCell style={{ fontWeight: 'bold', padding: '0px 16px' }}>{t('DC_7_TRAVER_MO_EXPENSE')}</TableCell>
            <TableCell style={{ fontWeight: 'bold', padding: '0px 16px' }}>{t('DC_7_REMARKS')}</TableCell>
            <TableCell style={{ padding: '0px 16px' }}></TableCell>
          </TableRow>
          <TableBody>
            <TableRow>
              <TableCell style={{ fontSize: '0.75rem', padding: '0px 16px' }}>{teamDetails?.teamName || '-'}</TableCell>
              <TableCell style={{ fontSize: '0.75rem', textAlign: 'center', padding: '0px 16px' }}>
                {teamDetails?.established}
              </TableCell>
              <TableCell style={{ fontSize: '0.75rem', textAlign: 'center', padding: '0px 16px' }}>
                {t(ageGroupNaming[teamDetails?.ageGroup]) || '-'}
              </TableCell>
              <TableCell style={{ fontSize: '0.75rem', padding: '0px 16px' }}>
                {getStaffSupervisor(teamDetails?.staffSupervisor, staffData) || '-'}
              </TableCell>
              <TableCell style={{ fontSize: '0.75rem', padding: '0px 16px' }}>
                {getStaffCoaches(teamDetails?.staffCoaches, staffData) || '-'}
              </TableCell>
              <TableCell style={{ fontSize: '0.75rem', padding: '0px 16px' }}>
                {getStaffGkCoaches(teamDetails?.staffGkCoaches, staffData) || '-'}
              </TableCell>
              <TableCell style={{ fontSize: '0.75rem', padding: '0px 16px' }}>
                {getMedicalStaff(teamDetails?.medicalStaff, staffData) || '-'}
              </TableCell>
              <TableCell style={{ fontSize: '0.75rem', padding: '0px 16px' }}>
                {getPracticeGrounds(teamDetails?.practiceGrounds, facilities) || '-'}
              </TableCell>
              <TableCell style={{ fontSize: '0.75rem', textAlign: 'center', padding: '0px 16px' }}>
                {teamFees?.admissionFees || '-'}
              </TableCell>
              <TableCell style={{ fontSize: '0.75rem', textAlign: 'center', padding: '0px 16px' }}>
                {teamFees?.playerMonthlyExpense || '-'}
              </TableCell>
              <TableCell style={{ fontSize: '0.75rem', textAlign: 'center', padding: '0px 16px' }}>
                {teamFees?.travelMonthlyExpense || '-'}
              </TableCell>
              <TableCell style={{ fontSize: '0.75rem', padding: '0px 16px' }}>{teamFees?.remarks || '-'}</TableCell>
              <TableCell style={{ padding: '0px 16px' }}></TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
});

TeamDetail.propTypes = {
  clubData: PropTypes.object,
};

const TrainingSchedule:any = React.memo(({ clubData, facilities }:any) => {
  const [t] = useTranslation();

  if (!clubData) return null;

  return (
    <div style={{ width: '100%' }}>
      <Typography sx={{ fontWeight: 'bold', fontFamily: 'Montserrat', padding: 2 }}>
        {t('DC_7_TRAINING_SCHEDULE')}
      </Typography>
      <Divider />
      <TableContainer sx={{ maxHeight: 300 }}>
        <Table stickyHeader aria-label="collapsible table">
          <TableRow className="sticky-header">
            <TableCell style={{ fontWeight: 'bold', padding: '5px 16px' }}>{t('DC_7_DAY')}</TableCell>
            <TableCell style={{ fontWeight: 'bold', padding: '5px 16px' }}>{t('DC_7_TRAINIG_END')}</TableCell>
            <TableCell style={{ fontWeight: 'bold', padding: '5px 16px' }}>{t('DC_7_GROUNDS')}</TableCell>
            <TableCell style={{ fontWeight: 'bold', padding: '5px 16px' }}>{t('DC_7_REMARKS')}</TableCell>
            <TableCell style={{ fontWeight: 'bold', padding: '5px 16px' }}>{t('DC_7_TRAINIG_START')}</TableCell>
          </TableRow>
          <TableBody>
            {Object.entries(clubData?.trainingSchedule ?? {}).map(([key, item]:any, index) => {
              // Skip if the key is 'id' or 'year'
              if (key === 'id' || key === 'year') {
                return null;
              }
              return (
                <TableRow key={index}>
                  <TableCell style={{ fontSize: '0.75rem', padding: '2px 16px' }}>{t(DayLabels[key]) || '-'}</TableCell>
                  <TableCell style={{ fontSize: '0.75rem', padding: '2px 16px' }}>{formatTime(item.start)}</TableCell>
                  <TableCell style={{ fontSize: '0.75rem', padding: '2px 16px' }}>{formatTime(item.end)}</TableCell>
                  <TableCell style={{ fontSize: '0.75rem', padding: '2px 16px' }}>
                    {getPracticeGrounds(item.grounds, facilities) || '-'}
                  </TableCell>
                  <TableCell style={{ fontSize: '0.75rem', padding: '2px 16px' }}>{item.remarks || '-'}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
});

TrainingSchedule.propTypes = {
  clubData: PropTypes.object,
};

const Competitions:any = React.memo(({ clubData }:any) => {
  const [t] = useTranslation();

  if (!clubData) return null;

  return (
    <div style={{ width: '100%' }}>
      <Typography sx={{ fontWeight: 'bold', fontFamily: 'Montserrat', padding: 2 }}>
        {t('DC_7_LY_COMPETITIONS')}
      </Typography>
      <Divider />
      <TableContainer sx={{ maxHeight: 300 }}>
        <Table stickyHeader aria-label="collapsible table">
          <TableRow className="sticky-header">
            <TableCell style={{ fontWeight: 'bold', padding: '5px 16px' }}>
              {new Date().getFullYear() - 1 + ' ' + t('DC_7_COMPETITION')}
            </TableCell>
            <TableCell style={{ fontWeight: 'bold', padding: '5px 16px' }}>{t('DC_7_POSITION_ACHIEVED')}</TableCell>
          </TableRow>
          <TableBody>
            {clubData?.competitions?.map((item:any, index:any) => (
              <TableRow key={index}>
                <TableCell style={{ fontSize: '0.75rem', padding: '2px 16px' }}>{item.competition || '-'}</TableCell>
                <TableCell style={{ fontSize: '0.75rem', padding: '2px 16px' }}>
                  {t(positionAchievedNaming[item.position]) || '-'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
});

Competitions.propTypes = {
  clubData: PropTypes.object,
};

const NumberOfPlayer:any = React.memo(({ clubData }:any) => {
  const [t] = useTranslation();

  if (!clubData) return null;

  const { teamDetails, playerAges } = clubData;
  return (
    <>
      <Typography sx={{ width: '33%', flexShrink: 0, fontWeight: 'bold', fontFamily: 'Montserrat', padding: 2 }}>
        {t('DC_7_PLAYER_AGES')}
      </Typography>
      <Divider />
      <TableContainer sx={{ maxHeight: 300 }}>
        <Table stickyHeader aria-label="collapsible table">
          <TableRow className="sticky-header">
            {playerAges &&
              Object.keys(playerAges)?.map((key, index) => {
                if (index === 0) {
                  return (
                    <TableCell key={index} style={{ fontWeight: 'bold', padding: '5px 16px' }}>
                      {t('NAME')}
                    </TableCell>
                  );
                }
                return (
                  <TableCell key={index} style={{ fontWeight: 'bold', textAlign: 'center', padding: '5px 16px' }}>
                    {key}
                  </TableCell>
                );
              })}
          </TableRow>
          <TableBody>
            <TableRow>
              {playerAges &&
                Object.values(playerAges)?.map((item:any, index) => {
                  if (index === 0) {
                    return (
                      <TableCell key={index} style={{ fontSize: '0.75rem', padding: '2px 16px' }}>
                        {teamDetails?.teamName || '-'}
                      </TableCell>
                    );
                  }
                  return (
                    <TableCell key={index} style={{ fontSize: '0.75rem', textAlign: 'center', padding: '2px 16px' }}>
                      {item || '-'}
                    </TableCell>
                  );
                })}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
});

NumberOfPlayer.propTypes = {
  clubData: PropTypes.object,
};

const Row = ({ index, selectedRow, setSelectedRow, team, facilities, staffData }:any) => {
  const handleClick = () => {
    setSelectedRow(index);
  };
  const [t] = useTranslation();

  return (
    <TableRow hover selected={index === selectedRow} onClick={handleClick}>
      <TableCell style={{ fontSize: '0.75rem', padding: '0px 16px', minWidth: '100px', maxWidth: '375px' }}>
        <ClubNameCell clubData={team} getImageUrl={(data:any) => data?.club?.imagePath} />
      </TableCell>
      <TableCell style={{ fontSize: '0.75rem', padding: '0px 16px', minWidth: '100px', maxWidth: '375px' }}>
        {team?.teamDetails?.teamName || '-'}
      </TableCell>
      <TableCell style={{ fontSize: '0.75rem', textAlign: 'center', padding: '0px 16px' }}>
        {team?.teamDetails?.established}
      </TableCell>
      <TableCell style={{ fontSize: '0.75rem', textAlign: 'center', padding: '0px 16px' }}>
        {t(ageGroupNaming[team?.teamDetails?.ageGroup]) || '-'}
      </TableCell>
      <TableCell style={{ fontSize: '0.75rem', padding: '0px 16px', minWidth: '100px', maxWidth: '375px' }}>
        {getStaffSupervisor(team?.teamDetails?.staffSupervisor, staffData) || '-'}
      </TableCell>
      <TableCell style={{ fontSize: '0.75rem', padding: '0px 16px' }}>
        {getPracticeGrounds(team?.teamDetails?.practiceGrounds, facilities) || '-'}
      </TableCell>
    </TableRow>
  );
};

Row.propTypes = {
  index: PropTypes.number.isRequired,
  selectedRow: PropTypes.number,
  setSelectedRow: PropTypes.func.isRequired,
  team: PropTypes.object.isRequired,
  facilities: PropTypes.array,
  staffData: PropTypes.array,
};

const TeamsExplorePage = () => {
  const [selectedRow, setSelectedRow] = useState(null);
  const [isAscending, setIsAscending] = useState(true);
  const [isAscending1, setIsAscending1] = useState(false);
  const [criteria, setCriteria] = useState('Club');
  const staffData = useSelector(getStaffData);
  const facilitiesData = useSelector(getFacilitiesData);
  const [t] = useTranslation();
  const teams:any = useSelector(getTeamsData);
  const searchValue = useSelector(getSearchValue);
  const [objectList, setObjectList] = useState(teams);
  const facilities: any = [];
  const [teamsCount, setTeamsCount] = useState();
  facilitiesData?.map((fac:any) => facilities.push(...fac.facilityGrounds));
  // Memoized sorting function
  const sortedObjectList = useMemo(() => {
    return [...objectList].sort((a:any, b:any) => {
      if (criteria === 'Club') {
        const nameA = a.club?.name.toLowerCase();
        const nameB = b.club?.name.toLowerCase();
        return isAscending ? nameA?.localeCompare(nameB) : nameB?.localeCompare(nameA);
      } else {
        const nameA = a.teamDetails?.teamName.toLowerCase();
        const nameB = b.teamDetails?.teamName.toLowerCase();
        return isAscending1 ? nameA?.localeCompare(nameB) : nameB?.localeCompare(nameA);
      }
    });
  }, [objectList, isAscending, isAscending1]);

  // Callback for sorting to avoid recreation on every render
  const handleSortClick1 = useCallback(() => {
    setCriteria('Team');
    setIsAscending1(prev => !prev);
  }, []);

  // Callback for sorting to avoid recreation on every render
  const handleSortClick = useCallback(() => {
    setCriteria('Club');
    setIsAscending(prev => !prev);
  }, []);

  useEffect(() => {
    if (searchValue !== '') {
      const filteredList:any = teams.filter((item:any) => {
        const searchLower = searchValue.toLowerCase();
        let staffSupervisor = getStaffSupervisor(item.teamDetails?.staffSupervisor, staffData);
        let practiceGrounds = getPracticeGrounds(item.teamDetails?.practiceGrounds, facilities);
        return (
          item.teamDetails?.teamName.toLowerCase().includes(searchLower) ||
          item.club?.name.toLowerCase().includes(searchLower) ||
          (item.teamDetails?.established && item.teamDetails.established.toString().includes(searchLower)) ||
          (item.teamDetails?.ageGroup && item.teamDetails.ageGroup.toString().includes(searchLower)) ||
          (staffSupervisor && staffSupervisor?.toString()?.includes(searchValue)) ||
          (practiceGrounds && practiceGrounds?.includes(searchValue)) ||
          (staffSupervisor &&
            staffSupervisor
              ?.toString()
              ?.toLowerCase()
              ?.includes(searchLower)) ||
          (practiceGrounds &&
            practiceGrounds
              ?.toString()
              ?.toLowerCase()
              ?.includes(searchLower))
        );
      });
      setObjectList(filteredList);
      setTeamsCount(filteredList?.length);
    } else {
      setObjectList(teams);
      setTeamsCount(teams?.length);
    }
  }, [searchValue, teams]);

  const rowsSkeleton = Array.from({ length: 10 }).map((_, index) => (
    <TableRow key={index}>
      {Array.from({ length: 6 }).map((_, cellIndex) => (
        <TableCell key={cellIndex}>
          <Skeleton variant="text" sx={{ fontSize: '1rem' }} width="100%" height={20} />
        </TableCell>
      ))}
    </TableRow>
  ));

  return (
    <>
      <Typography sx={{ width: '33%', flexShrink: 0, fontWeight: 'bold', fontFamily: 'Montserrat', padding: '6px' }}>
        {t('DC_7_TEAM_DETAILS') + ` (${teamsCount} ${t('ROWS')})`}
      </Typography>
      <Divider />
      <TableContainer className={'table-container'} sx={{ maxHeight: '600px' }}>
        <Table sx={{ width: 'max-content', boxShadow: 'none' }} stickyHeader aria-label="collapsible table">
          <TableRow className="sticky-header">
            <TableCell style={{ fontWeight: 'bold', padding: '6px 16px' }}>
              {t('CLUB_NAME')}
              <span onClick={handleSortClick} style={{ cursor: 'pointer' }}>
                {!isAscending ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
              </span>
            </TableCell>
            <TableCell style={{ fontWeight: 'bold', padding: '6px 16px' }}>
              {t('TEAM_NAME')}
              <span onClick={handleSortClick1} style={{ cursor: 'pointer' }}>
                {isAscending1 ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
              </span>
            </TableCell>
            <TableCell style={{ fontWeight: 'bold', padding: '6px 16px' }}>{t('DC_7_ESTABLISHED')}</TableCell>
            <TableCell style={{ fontWeight: 'bold', textAlign: 'center', whiteSpace: 'nowrap', padding: '6px 16px' }}>
              {t('DC_7_AGE_GROUP')}
            </TableCell>
            <TableCell style={{ fontWeight: 'bold', whiteSpace: 'nowrap', padding: '6px 16px' }}>{t('DC_7_STAFF_SUPERVISOR')}</TableCell>
            <TableCell style={{ fontWeight: 'bold', padding: '6px 16px' }}>{t('DC_7_PRACTICE_GROUNDS')}</TableCell>
          </TableRow>
          <TableBody>
            {teams && teams.length > 0 ? (
              sortedObjectList.length > 0 ? (
                sortedObjectList.map((team, index) => (
                  <Row
                    key={index}
                    index={index}
                    selectedRow={selectedRow}
                    setSelectedRow={setSelectedRow}
                    team={team}
                    facilities={facilities}
                    staffData={staffData}
                  />
                ))
              ) : searchValue === '' ? (
                rowsSkeleton
              ) : (
                <TableRow>
                  <TableCell colSpan={6} style={{ textAlign: 'center' }}>
                    {t('No_Matching_Records_Found')}...
                  </TableCell>
                </TableRow>
              )
            ) : teams?.length == 0 ? (
              <TableRow>
                <TableCell colSpan={6} style={{ textAlign: 'center' }}>
                  {t('No_Matching_Records_Found')}...
                </TableCell>
              </TableRow>
            ) : (
              rowsSkeleton
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {selectedRow != null ? (
        <>
          <div style={{ paddingTop: '20px' }}>
            <TeamDetail
              clubData={objectList[selectedRow]}
              staffData={staffData}
              facilities={facilities}
              setSelectedRow={setSelectedRow}
            />
          </div>
          <div style={{ paddingTop: '20px', display: 'flex', gap: '20px' }}>
            <TrainingSchedule clubData={objectList[selectedRow]} facilities={facilities} />
            <Divider orientation="vertical" flexItem />
            <Competitions clubData={objectList[selectedRow]} />
          </div>
          <div style={{ paddingTop: '20px' }}>
            <NumberOfPlayer clubData={objectList[selectedRow]} />
          </div>
        </>
      ) : (
        ''
      )}
    </>
  );
};

export default TeamsExplorePage;
